/* eslint-disable indent */
/**
 * Implements a CEX info block
 */
import Image from '../../../atoms/Image';
import Text from '../../../atoms/Text';
import InfoCoin from '../../../molecules/InfoCoin';
import CoinInfoField from '../CoinInfoField';
import { toFixed2 } from '../../DexTrades/formatters';
import { formatUSD } from '../../Modal/TickersModal/helpers';
import { ICurrentState } from '../../../reducers/current';
import { ITickerLivePrice } from '../../Watchlist/hooks/useTickerLivePrice';
import NumberAnimation from '../../../molecules/NumberAnimation';
import NumberDisplay from '../../../molecules/NumberDisplay';

import coingecko from '../../../assets/images/coingecko.svg';
import coinmarketcap from '../../../assets/images/coinmarketcap.svg';
import twitter from '../../../pages/TokenPage/images/twitter.svg';
import telegram from '../../../pages/TokenPage/images/telegram.svg';
import { IPriceChange } from '../../../helpers/priceChange';

const decorateValue = (key: string, value: string | number): string => {
  switch (key) {
    case 'Daily volume':
    case 'Fully diluted valuation':
    case 'Market Cap':
      return formatUSD(Math.round(+value));
    case 'Circ. Supply in %':
      return `${toFixed2(value)} %`;
    default:
      return value?.toString() ?? '';
  }
};

interface ICexCoinInfoProps
  extends ITickerLivePrice,
    ICurrentState,
    IPriceChange {
  info: Record<string, string | number>;
  id: number;
  title: string;
  slug: string;
  coingecko_id: string;
  coinmarketcap_id: string;
  logo_url: string;
  website: string;
  telegram: string;
  twitter_id: string;
}

const CexCoinInfo = (props: ICexCoinInfoProps) => {
  let priceColor = 'text--grey-200';

  if (props.side === 'buy') {
    priceColor = 'text--green';
  } else if (props.side === 'sell') {
    priceColor = 'text--red';
  }

  return (
    <>
      <div className='info_coin__mt'>
        <InfoCoin {...props} isCEX />
      </div>
      <div className='row'>
        <div className='info_coin__icons'>
          {props.website && (
            <a
              href={props.website}
              className='icon icon_link_img icon_link_img--font icon-discovery'
              target='_blank'
              rel='noreferrer'
            />
          )}
          {props.coingecko_id && (
            <a
              href={`https://www.coingecko.com/en/coins/${props.coingecko_id}`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={coingecko} size='s' />
            </a>
          )}
          {props.coinmarketcap_id && (
            <a
              href={`https://coinmarketcap.com/currencies/${props.coinmarketcap_id}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={coinmarketcap} size='s' />
            </a>
          )}
          {props.telegram && (
            <a
              href={`https://t.me/${props.telegram}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={telegram} size='s' />
            </a>
          )}
          {props.twitter_id && (
            <a
              href={`https://twitter.com/${props.twitter_id}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={twitter} size='s' />
            </a>
          )}
        </div>
        <div className='fragment info_coin__price'>
          <NumberAnimation
            value={props.price.rawPrice}
            round={2}
            startSymbol={props.price.sign}
            textProps={{
              size: 18,
              className: priceColor,
            }}
          />
          {props.priceChange === 0 ? (
            <Text size={12} className='info_coin__price__change text--grey-200'>
              (0%) $0
            </Text>
          ) : (
            <Text
              size={12}
              className={`info_coin__price__change ${
                props.priceChangePercent < 0 ? 'text--red' : 'text--green'
              }`}
            >
              <NumberDisplay
                value={props.priceChangePercent}
                round={2}
                startSymbol='('
                endSymbol='%)'
                string
              />
              &nbsp;
              <NumberDisplay
                value={props.priceChange}
                round={2}
                startSymbol='$'
                string
              />
            </Text>
          )}
        </div>
      </div>
      {Object.keys(props.info).map((key) => (
        <CoinInfoField
          key={key}
          name={key}
          value={decorateValue(key, props.info[key])}
        />
      ))}
    </>
  );
};

export default CexCoinInfo;
